import type { FormEvent } from 'react'
import { useState } from 'react'
import { InviteMemberRequest, NewPassword, type Role } from '@rp/contracts'
import { isApiError } from '../../api/client'
import { Button, Field, Segmented } from '../../design'
import { SETTINGS_COPY } from './copy'
import { useInviteMember } from './memberQueries'
import { PasswordInput } from './PasswordInput'
import { MEMBER_PASSWORD_COPY } from './passwordCopy'
import { PasswordReveal, type RevealedPassword } from './PasswordReveal'
import { ROLE_OPTIONS } from './roleOptions'

type InviteDraft = { email: string; displayName: string; role: Role; password: string }

type Problems = { email?: string; password?: string }

const EMPTY_DRAFT: InviteDraft = { email: '', displayName: '', role: 'reviewer', password: '' }

const requestOf = (draft: InviteDraft, withPassword: boolean) =>
  InviteMemberRequest.safeParse({
    email: draft.email.trim(),
    role: draft.role,
    ...(draft.displayName.trim() === '' ? {} : { displayName: draft.displayName.trim() }),
    ...(withPassword ? { password: draft.password } : {}),
  })

const passwordProblem = (password: string): Problems => {
  if (password === '') return { password: MEMBER_PASSWORD_COPY.required }
  return NewPassword.safeParse(password).success ? {} : { password: MEMBER_PASSWORD_COPY.tooShort }
}

const problemsOf = (draft: InviteDraft, withPassword: boolean): Problems => ({
  ...(requestOf({ ...draft, password: '' }, false).success ? {} : { email: SETTINGS_COPY.inviteEmailInvalid }),
  ...(!withPassword ? {} : passwordProblem(draft.password)),
})

const useInviteSubmission = () => {
  const invite = useInviteMember()
  const [draft, setDraft] = useState<InviteDraft>(EMPTY_DRAFT)
  const [problems, setProblems] = useState<Problems>({})
  const [revealed, setRevealed] = useState<RevealedPassword | null>(null)

  const send = (withPassword: boolean) => {
    const found = problemsOf(draft, withPassword)
    setProblems(found)
    const parsed = requestOf(draft, withPassword)
    if (Object.keys(found).length > 0 || !parsed.success) return
    setRevealed(null)
    invite.mutate(parsed.data, {
      onSuccess: (member) => {
        if (parsed.data.password !== undefined) setRevealed({ email: member.email, password: parsed.data.password })
        setDraft(EMPTY_DRAFT)
      },
    })
  }
  return { invite, draft, setDraft, problems, revealed, setRevealed, send }
}

export const InviteMemberForm = () => {
  const { invite, draft, setDraft, problems, revealed, setRevealed, send } = useInviteSubmission()
  const serverError = isApiError(invite.error) ? invite.error : undefined
  const emailError = problems.email ?? serverError?.message
  const refusedPassword = serverError?.code === 'PASSWORD_NOT_SETTABLE'

  const onSubmit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault()
    send(true)
  }

  return (
    <form className="invite-form" onSubmit={onSubmit} noValidate aria-labelledby="invite-heading">
      <h3 id="invite-heading">{SETTINGS_COPY.inviteTitle}</h3>
      <p className="settings-hint measure">{SETTINGS_COPY.inviteHint}</p>
      <div className="settings-pair">
        <Field id="invite-email" label={SETTINGS_COPY.inviteEmail} {...(emailError === undefined ? {} : { error: emailError })}>
          {({ id, describedBy }) => (
            <input
              id={id}
              type="email"
              autoComplete="off"
              inputMode="email"
              aria-describedby={describedBy}
              aria-invalid={emailError === undefined ? undefined : true}
              value={draft.email}
              onChange={(event) => setDraft({ ...draft, email: event.target.value })}
            />
          )}
        </Field>
        <Field id="invite-name" label={SETTINGS_COPY.inviteName}>
          {({ id }) => (
            <input
              id={id}
              autoComplete="off"
              value={draft.displayName}
              onChange={(event) => setDraft({ ...draft, displayName: event.target.value })}
            />
          )}
        </Field>
      </div>
      <Segmented
        label={SETTINGS_COPY.inviteRole}
        name="invite-role"
        value={draft.role}
        options={ROLE_OPTIONS}
        onChange={(role) => setDraft({ ...draft, role })}
      />
      <PasswordInput
        id="invite-password"
        value={draft.password}
        error={problems.password}
        onValue={(password) => setDraft({ ...draft, password })}
      />
      <div className="settings-save">
        <Button type="submit" variant="primary" pending={invite.isPending} pendingLabel={SETTINGS_COPY.inviting}>
          {SETTINGS_COPY.inviteSubmit}
        </Button>
        {refusedPassword ? <Button onClick={() => send(false)}>{MEMBER_PASSWORD_COPY.inviteWithout}</Button> : null}
        {invite.isSuccess ? (
          <p className="settings-saved text-tone tone-success" role="status">
            {SETTINGS_COPY.invited(invite.data.email)}
          </p>
        ) : null}
      </div>
      {revealed === null ? null : <PasswordReveal revealed={revealed} onDismiss={() => setRevealed(null)} />}
    </form>
  )
}
